import BusinessPermissions from "../../../Adviser/Constants/BusinessPermissions";
import { RATE_FIELDS } from "../../../Adviser/Tabs/Rates/rateFields";

export const CONTRACT_APPOINTMENT_OPTIONS = Object.freeze([
  "Financial Planning Consultant",
  "Mortgage and Protection Appointed Representative",
  "General Insurance Appointed Representative",
]);

export const CONTRACT_PERMISSION_OPTIONS = Object.freeze([
  Object.freeze({
    value: BusinessPermissions.FP,
    label: "Investment Arrangements",
  }),
  Object.freeze({
    value: BusinessPermissions.GI,
    label: "Insurance-Related Activities",
  }),
  Object.freeze({
    value: BusinessPermissions.MA,
    label: "Home Finance Transactions",
  }),
  Object.freeze({
    value: BusinessPermissions.ER,
    label: "Equity Release Activities",
  }),
  Object.freeze({
    value: BusinessPermissions.LTC,
    label: "Long-Term Care Planning",
  }),
]);

export const CONTRACT_RATE_FIELDS = Object.freeze(RATE_FIELDS);

// Populate these values with the standard contract rates when agreed.
export const DEFAULT_CONTRACT_RATES = Object.freeze(
  RATE_FIELDS.reduce(
    (rates, field) => ({
      ...rates,
      [field.key]: field.defaultValue ?? "",
    }),
    {},
  ),
);

export const CONTRACT_SUPERVISION_FEE_VALUES = Object.freeze({
  generalInsuranceOrMortgage: Object.freeze({
    value: "generalInsuranceOrMortgage",
    label: "General Insurance or Mortgage Regulated Individual",
    weeklyFee: 75,
    amountLabel: "£75 per week",
  }),
  financialPlanningConsultant: Object.freeze({
    value: "financialPlanningConsultant",
    label: "Financial Planning Consultant",
    weeklyFee: 145,
    amountLabel: "£145 per week",
  }),
});

export const CONTRACT_SUPERVISION_FEE_OPTIONS = Object.freeze([
  Object.freeze({
    value: "generalInsuranceOrMortgage",
    label:
      "£75 per week for each and every General Insurance or Mortgage Regulated Individual",
    weeklyFee: 75,
  }),
  Object.freeze({
    value: "financialPlanningConsultant",
    label: "£145 per week for each and every Financial Planning Consultant",
    weeklyFee: 145,
  }),
]);

export const getSelectedSupervisionFees = (fees = {}) =>
  CONTRACT_SUPERVISION_FEE_OPTIONS.filter(
    ({ value }) => fees?.[value]?.selected === true,
  ).map(({ value }) => value);

export const getPermittedActivitiesInfo = (permittedActivities = []) => {
  const selected =
    permittedActivities && permittedActivities.length
      ? permittedActivities
      : CONTRACT_PERMISSION_OPTIONS.map(({ value }) => value);

  const activityLabels = selected
    .map((value) => {
      const option = CONTRACT_PERMISSION_OPTIONS.find(
        (item) => item.value === value,
      );
      return option?.label || value;
    })
    .filter(Boolean);

  const summary =
    activityLabels.length === 0
      ? "The activities are: none."
      : `The activities are: ${activityLabels.join(", ")}.`;

  return {
    selected,
    activityLabels,
    summary,
  };
};

export const buildSupervisionFeeMap = (selectedValues = []) =>
  CONTRACT_SUPERVISION_FEE_OPTIONS.reduce(
    (fees, option) => ({
      ...fees,
      [option.value]: {
        selected: selectedValues.includes(option.value),
        weeklyFee: option.weeklyFee,
      },
    }),
    {},
  );

export const getSupervisionFeeSummary = (fees = {}) => {
  const selected = getSelectedSupervisionFees(fees);

  if (!selected.length) {
    return {
      text: "The current fee is calculated at £75 per week for each and every General Insurance or Mortgage Regulated Individual and £145 per week for each and every Financial Planning Consultant.",
      selection: [],
    };
  }

  const details = selected.map(
    (value) => CONTRACT_SUPERVISION_FEE_VALUES[value],
  );

  const first = details[0];
  const second = details[1];

  const feeText =
    details.length === 2
      ? `The current fee is calculated at ${first.amountLabel} for each and every ${first.label} and ${second.amountLabel} for each and every ${second.label}.`
      : `The current fee is calculated at ${first.amountLabel} for each and every ${first.label}.`;

  if (
    selected.includes("financialPlanningConsultant") &&
    selected.includes("generalInsuranceOrMortgage")
  ) {
    return {
      text: `${feeText} If both are selected, the fee will be treated as a Financial Planning Consultant fee.`,
      selection: selected,
    };
  }

  if (selected.includes("generalInsuranceOrMortgage")) {
    return {
      text: `${feeText} If only the Mortgage/General Insurance fee is selected, the fee will be treated as the Mortgage/General Insurance rate.`,
      selection: selected,
    };
  }

  return {
    text: `${feeText} If only the Financial Planning Consultant fee is selected, the fee will be treated as the Financial Planning Consultant rate.`,
    selection: selected,
  };
};

// Add contract-wide fallback values here. Adviser Directory values take priority.
export const DEFAULT_CONTRACT_VALUES = Object.freeze({
  contractStartDate: "",
  fcaNumber: "",
  businessAddress: Object.freeze({ fullAddress: "" }),
  appointmentStatuses: Object.freeze([]),
  lawOfContractNames: Object.freeze([""]),
  supervisionFees: Object.freeze({
    generalInsuranceOrMortgage: Object.freeze({
      selected: false,
      weeklyFee: 75,
    }),
    financialPlanningConsultant: Object.freeze({
      selected: false,
      weeklyFee: 145,
    }),
  }),
  permittedActivities: Object.freeze([]),
  commissionFeeTypes: Object.freeze([]),
  useDefaultRates: false,
  fileName: "",
  approved: false,
});

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const normaliseContractPermissions = (value) => {
  const permissions = asArray(value);
  const mapped = permissions.map((permission) => {
    const key = String(permission).trim().toLowerCase();
    if (["fp", "financial planning", "investment arrangements"].includes(key))
      return BusinessPermissions.FP;
    if (
      [
        "gi",
        "prot",
        "general insurance",
        "protection",
        "insurance-related activities",
      ].includes(key)
    )
      return BusinessPermissions.GI;
    if (["ma", "mortgages", "home finance transactions"].includes(key))
      return BusinessPermissions.MA;
    if (["er", "equity release", "equity release activities"].includes(key))
      return BusinessPermissions.ER;
    if (["ltc", "long term care", "long-term care planning"].includes(key))
      return BusinessPermissions.LTC;
    return permission;
  });

  return [...new Set(mapped)];
};

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") || "";

const getStoredRates = (savedRates = {}) =>
  RATE_FIELDS.reduce(
    (rates, field) => ({
      ...rates,
      [field.key]: savedRates[field.key] ?? "",
    }),
    {},
  );

export const buildContractDetails = ({
  contractType,
  applicantName,
  directoryDetails = {},
}) => {
  const adviser = directoryDetails.adviserDoc || {};
  const personal = directoryDetails.personalDetails || {};
  const application = directoryDetails.applicationDetails || {};

  const tradingName = firstValue(
    adviser.tradingName,
    application.tradingStyle,
    adviser.tradingStyle,
    adviser.brandName,
  );
  const organisationName =
    contractType === "Ltd Company"
      ? adviser.tradingName || ""
      : firstValue(
          contractType === "Trading Style" ? tradingName : "",
          adviser.companyName,
          tradingName,
          applicantName,
        );

  return {
    ...DEFAULT_CONTRACT_VALUES,
    contractType,
    applicantName,
    ...(contractType === "Ltd Company"
      ? { tradingName: adviser.tradingName || "" }
      : { organisationName }),
    fcaNumber: personal.fcaNumber || "",
    businessAddress: {
      fullAddress: personal.businessAddress?.fullAddress || "",
    },
    appointmentStatuses: asArray(adviser.status),
    permittedActivities: normaliseContractPermissions(
      application.businessAreas || application.ARBusinessAreas,
    ),
    rates: getStoredRates(directoryDetails.rates || {}),
    fileName: `${organisationName || applicantName || contractType} Contract`,
  };
};
